import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from "react-helmet";
import { useLocation, useParams } from 'react-router-dom';
import SessionConstants from '../../config/SessionConstants.js';
import BreadCrumbs from '../BreadCrumbs';
import { Settings } from '@mui/icons-material';
import { Button } from '@mui/material';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import AssignedInterpreters from './AssignedInterpreters/index.js';
import VariantSettingsModal from './VariantSettingsModal/index.js';
import VariantReviewHistory from './VariantReviewHistory/index.js';
import APIUtils from '../../utils/APIUtils';
import ReviewStatus from '../../config/ReviewStatus.js';
import Loading from '../Loading';
import './index.css';

export default function VariantDashboard() {
  const location = useLocation();
  const { variantId: paramVariantId } = useParams();

  // Extract state from location
  const {
    variantId: stateVariantId,
    variantName: stateVariantName,
    breadCrumbs: stateBreadCrumbs = [],
  } = location.state || {};

  // Prefer state variantId, fallback to URL param
  const variantId = stateVariantId || paramVariantId;
  const variantName = stateVariantName || '';

  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [variantSettingsModalIsOpen, setVariantSettingsModalIsOpen] = useState(false);
  const [requiredInterpretations, setRequiredInterpretations] = useState(null);
  const [variantReview, setVariantReview] = useState(null);
  const [interpreterData, setInterpreterData] = useState([]);
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [showVariantSetting, setShowVariantSetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [childComponentKey, setChildComponentKey] = useState(0);

  // Load passed params for breadcrumbs, etc.
  useEffect(() => {
    let crumbs = stateBreadCrumbs || [];
    crumbs = [...crumbs, {
      name: 'VariantDashboard',
      variantId: variantId,
      variantName: variantName,
    }];
    setBreadCrumbs(crumbs);
  }, [stateBreadCrumbs, variantId, variantName]);

  // Load data on mount and when variantId changes
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await loadData();
      setLoading(false);
    }
    fetchData();
  }, [variantId]);

  const loadData = useCallback(async () => {
    const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
    const variantResponse = await APIUtils.getReview(programId, variantId);
    const variantReview = variantResponse;
    const interpretations = await APIUtils.getInterpretationsByReview(variantReview.variantReviewId);
    const showVariantSetting = (
      variantReview.reviewStatus === ReviewStatus.REVIEW_CREATED ||
      variantReview.reviewStatus === ReviewStatus.IN_PROGRESS
    );
    const modifiedInterpreterData = interpretations.map(item => ({
      ...item,
      completed: item.interpretationStatus === 'Completed',
      inProgress: item.interpretationStatus !== 'Completed'
    }));
    setVariantReview(variantReview);
    setRequiredInterpretations(variantReview.requiredInterpretations);
    setInterpreterData(modifiedInterpreterData);
    setReviewHistoryData(variantReview.historyItems);
    setShowVariantSetting(showVariantSetting);
    setChildComponentKey(prev => prev + 1);
  }, [variantId]);

  const openModal = () => setVariantSettingsModalIsOpen(true);
  const closeModal = () => setVariantSettingsModalIsOpen(false);

  const refresh = async () => {
    setLoading(true);
    await loadData();
    setLoading(false);
  };

  return (
    <div className="managerSettings">
      <Loading isLoading={loading} />
      <Helmet>
        <title>ED3N - Variant Review Dashboard</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Variant Review Dashboard</h1>
      <div className="managerButtonBar">
        <div className="managerLeftName">
          Variant {variantName}
        </div>
        {showVariantSetting ? (
          <div className="managerButtons">
            <Button
              id="variantReviewSettingsButton"
              color="primary"
              variant="contained"
              aria-label="Variant Review Settings"
              onClick={openModal}
              startIcon={<Settings />}
            >
              Variant Review Settings
            </Button>
          </div>
        ) : ''}
      </div>
      <Grid container spacing={3} className="dashGridContainer">
        <Grid item xs={12}>
          <Paper className="card userDashTable" elevation={0}>
            <div className="requiredInterpretations">Required Interpretations: {requiredInterpretations}</div>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper className="card userDashTable" elevation={0}>
            <div className="dashTableDescription">Assigned Interpreters</div>
            {interpreterData && interpreterData.length > 0 ? (
              <AssignedInterpreters
                data={interpreterData}
                childKey={childComponentKey}
              />
            ) : (
              'No Interpreters Assigned'
            )}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper className="card userDashTable" elevation={0}>
            <div className="dashTableDescription">Variant Review History</div>
            {reviewHistoryData && reviewHistoryData.length > 0 ? (
              <VariantReviewHistory
                data={reviewHistoryData}
                childKey={childComponentKey}
              />
            ) : (
              'No Variant Review History'
            )}
          </Paper>
        </Grid>
      </Grid>
      {variantReview ? (
        <VariantSettingsModal
          closeModal={closeModal}
          modalIsOpen={variantSettingsModalIsOpen}
          refresh={refresh}
          reviewData={variantReview}
          interpretationData={interpreterData}
          key={childComponentKey}
        />
      ) : ('')}
    </div>
  );
}